// AccountSelector.tsx
import * as React from 'react';
import { alpha, Avatar, Box, ButtonBase, IconButton, InputBase, ListItem, ListItemAvatar, ListItemButton, ListItemText, MenuList, Paper, Popover, Stack, Typography, useTheme } from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import senaUserLogo from '../../../assets/paige.jpeg';
import { IUser } from '../slice/types';

export type Account = {
  id: string;
  name: string; // e.g., “Acme Inc”

  avatarUrl?: string;
};

type AccountSelectorProps = {
  accounts: Account[];
  selectedId: string;
  onChange: (id: string) => void;
  placeholder?: string; // text shown when no selection found
  size?: 'sm' | 'md'; // compactness of the trigger
  sx?: any; // optional sx overrides for the trigger
  isSidebarOpen: boolean;
  currentUser: IUser | null;
};

export function AccountSelector({ accounts, selectedId, onChange, placeholder = 'Select account', size = 'md', sx, isSidebarOpen, currentUser }: AccountSelectorProps) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [query, setQuery] = React.useState('');
  const open = Boolean(anchorEl);

  const selected = accounts.find((a) => a.id === selectedId) || null;

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter((a) => [a.name].filter(Boolean).some((text) => (text as string).toLowerCase().includes(q)));
  }, [accounts, query]);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleSelect = (id: string) => {
    onChange(id);
    handleClose();
  };

  const avatarSize = 40;

  return (
    <>
      {/* Trigger */}

      <ButtonBase
        onClick={(e) => handleOpen(e)}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : undefined}
        aria-controls={open ? 'account-selector-popover' : undefined}
        sx={{
          px: isSidebarOpen ? 2 : 'auto',
          py: 1.5,
          gap: 1,
          outline: 'none',
          // boxShadow: theme.palette.mode === 'light' ? `0 1px 1px ${alpha('#000', 0.05)}` : `0 1px 1px ${alpha('#000', 0.7)}`,
          transition: 'box-shadow .15s ease, background-color .15s ease, border-color .15s ease',

          '&:focus-visible': {
            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.25)}`,
          },
          display: 'flex',
          justifyContent: 'space-between',
          ...sx,
        }}
        disabled={currentUser?.productId !== null}
      >
        <Box sx={{ textAlign: 'left', minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar
            src={currentUser?.productId === null ? selected?.avatarUrl : senaUserLogo}
            alt={selected?.name}
            sx={{
              width: avatarSize,
              height: avatarSize,
              fontSize: avatarSize * 0.45,
              bgcolor: 'primary.main',
              borderRadius: '0.8rem',
              boxShadow: 'none',
            }}
          >
            {(selected?.name || placeholder).slice(0, 1).toUpperCase()}
          </Avatar>
          {isSidebarOpen && (
            <Typography variant={'h6'} noWrap sx={{ lineHeight: 1.2, fontWeight: 600, maxWidth: 160 }} title={selected?.name || placeholder}>
              {currentUser?.productId === null ? selected?.name || placeholder : 'Sena'}
            </Typography>
          )}
        </Box>

        {isSidebarOpen && !currentUser?.productId && <KeyboardArrowDownRoundedIcon fontSize="small" />}
      </ButtonBase>

      {/* Popover */}
      <Popover
        id="account-selector-popover"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              width: 320,
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: theme.palette.mode === 'light' ? '0 10px 30px rgba(0,0,0,.12), 0 4px 10px rgba(0,0,0,.06)' : '0 10px 30px rgba(0,0,0,.6), 0 4px 10px rgba(0,0,0,.4)',
            },
          },
        }}
      >
        {/* Search bar */}
        <Paper
          elevation={0}
          sx={{
            m: 1,
            px: 1,
            py: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderRadius: 1.5,
            border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
            background: theme.palette.mode === 'light' ? alpha(theme.palette.background.default, 0.6) : alpha(theme.palette.background.paper, 0.08),
          }}
        >
          <SearchRoundedIcon fontSize="small" />
          <InputBase autoFocus placeholder="Search products…" value={query} onChange={(e) => setQuery(e.target.value)} sx={{ flex: 1, fontSize: 14 }} inputProps={{ 'aria-label': 'Search products' }} />
          {query && (
            <IconButton size="small" aria-label="Clear" onClick={() => setQuery('')}>
              {/* small × using the font itself to avoid extra icon import */}
              <Box component="span" sx={{ fontSize: 16, lineHeight: 1, fontWeight: 700 }}>
                ×
              </Box>
            </IconButton>
          )}
        </Paper>

        <MenuList
          dense
          autoFocusItem={false}
          sx={{
            py: 0.5,
            maxHeight: 360,
            overflowY: 'auto',
          }}
        >
          {filtered.length === 0 && (
            <ListItem dense>
              <ListItemText primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} primary="No matches" />
            </ListItem>
          )}

          {filtered.map((acc) => {
            const selected = acc.id === selectedId;
            return (
              <ListItemButton
                key={acc.id}
                onClick={() => handleSelect(acc.id)}
                selected={selected}
                sx={{
                  borderRadius: 1.25,
                  mx: 1,
                  my: 0.25,
                  '&.Mui-selected': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.12) },
                  },
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    src={acc.avatarUrl}
                    alt={acc.name}
                    sx={{
                      width: 28,
                      height: 28,
                      bgcolor: 'primary.main',
                      fontSize: 12,
                      borderRadius: '0.8rem',
                    }}
                  >
                    {acc.name.slice(0, 1).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight={600} noWrap title={acc.name}>
                        {acc.name}
                      </Typography>
                    </Stack>
                  }
                />
                {selected && <CheckRoundedIcon fontSize="small" />}
              </ListItemButton>
            );
          })}
        </MenuList>
      </Popover>
    </>
  );
}
